import { useQuery } from '@tanstack/react-query'
import { useRouterState } from '@tanstack/react-router'
import { useEffect } from 'react'
import { api } from '../lib/api.js'
import { useSession } from '../lib/session.js'

const APP = 'Feral Mode Live'
const PROFILE = /^\/u\/([^/]+)$/

/**
 * The window title for each screen: "Chat · Feral Mode Live". A profile shows the person's handle
 * (Jason asked for it); everything else names the screen, not a person or a place. The quick exit
 * (WF-SAF-04) is a weather page, so its title is just that.
 */
const TITLES: [RegExp, string | ((tab: string | undefined) => string | null)][] = [
  [/^\/(enter)?$/, () => null],
  [/^\/leave$/, () => 'Weather'],
  [/^\/age$/, 'Age check'],
  [/^\/anon$/, 'Enter anonymously'],
  [/^\/i\/[^/]+$/, 'Your invite'],
  [/^\/join$/, 'Create account'],
  [/^\/(rules|you\/rules)$/, 'The rules'],
  [/^\/install$/, 'Install'],
  [/^\/location$/, 'Location'],
  [/^\/notify$/, 'Notifications'],
  [/^\/sign-in(\/verify|\/passkey)?$/, 'Sign in'],
  [/^\/continue$/, 'Continue from Safari'],
  [/^\/map$/, 'Map'],
  [/^\/grid$/, 'Grid'],
  [/^\/wall$/, (tab) => (tab === 'saved' ? 'Saved' : 'Wall')],
  [/^\/wall\/saved$/, 'Saved'],
  [
    /^\/gate$/,
    (tab) =>
      tab === 'vetted'
        ? 'Your vetting'
        : tab === 'places'
          ? 'Your places'
          : tab === 'chain'
            ? 'Your chain'
            : 'Gate',
  ],
  [/^\/gate\/record$/, 'Record your clips'],
  [/^\/gate\/review\/[^/]+$/, 'Review'],
  [
    /^\/inbox$/,
    (tab) =>
      ({ taps: 'Taps', requests: 'Requests', knocks: 'Requests', activity: 'Activity' })[tab ?? ''] ?? 'Chat',
  ],
  [/^\/inbox\/[^/]+$/, 'Conversation'],
  [/^\/you$/, 'You'],
  [/^\/settings\/notifications$/, 'Notifications'],
  [/^\/settings\/quick-replies$/, 'Quick replies'],
  [/^\/plus$/, 'Feral Mode Plus'],
  [/^\/account$/, 'Account'],
  [/^\/account\/data$/, 'Your data'],
  [/^\/account\/recovery$/, 'Keep your account'],
  [/^\/you\/activity$/, 'Activity'],
  [/^\/you\/favorites$/, 'Favorites'],
  [
    /^\/you\/gatherings$/,
    (tab) => ({ groups: 'Your groups', events: 'Your events' })[tab ?? ''] ?? 'Favorites',
  ],
  [/^\/you\/notes$/, 'Notes'],
  [/^\/you\/viewers$/, 'Who looked'],
  [/^\/you\/profile$/, 'Your profile'],
  [/^\/you\/albums\/[^/]+$/, 'Album'],
  [/^\/you\/visibility$/, 'Visibility'],
  [/^\/you\/reports$/, 'Your reports'],
  [/^\/you\/blocked$/, 'Blocked'],
  [/^\/invites$/, 'Invites'],
  [/^\/invites\/new$/, 'New invite'],
  [/^\/invites\/shared\/new$/, 'New shared code'],
  [/^\/invites\/shared\/[^/]+$/, 'Shared code'],
  [/^\/invites\/[^/]+$/, 'Invite'],
  [/^\/chain$/, 'Your chain'],
  [/^\/report$/, 'Report'],
  [/^\/safety(\/[^/]+)?$/, 'Safety Center'],
  [/^\/about(\/[^/]+)?$/, 'About'],
  [/^\/you\/standing$/, 'Your standing'],
  [/^\/spots\/new$/, 'Add a spot'],
  [/^\/spots\/[^/]+\/edit$/, 'Edit spot'],
  [/^\/spots\/[^/]+$/, 'Spot'],
  [/^\/u\/[^/]+$/, 'Profile'],
]

export function titleFor(path: string, tab?: string, handle?: string | null): string {
  if (handle && PROFILE.test(path)) return `${handle} · ${APP}`
  const hit = TITLES.find(([re]) => re.test(path))
  const name = hit ? (typeof hit[1] === 'string' ? hit[1] : hit[1](tab)) : null
  if (name === 'Weather') return name
  return name ? `${name} · ${APP}` : APP
}

/** Rendered once, in the root route: keeps document.title in step with the screen. */
export function RouteTitle() {
  const { me } = useSession()
  const path = useRouterState({ select: (s) => s.location.pathname })
  const tab = useRouterState({ select: (s) => (s.location.search as { tab?: unknown }).tab })
  // The profile the screen itself loads (FullProfile: same key, same options), so no second request.
  const profileId = PROFILE.exec(path)?.[1]
  const profile = useQuery({
    queryKey: ['user', profileId],
    queryFn: () => api.user(profileId as string),
    enabled: !!me && !!profileId,
    retry: false,
  })
  const handle = profileId ? profile.data?.handle : null
  useEffect(() => {
    document.title = titleFor(path, typeof tab === 'string' ? tab : undefined, handle)
  }, [path, tab, handle])
  return null
}
